import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  Wrap,
  WrapItem,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { SelectOptionType } from 'components/FormRemoteSelectInput';
import InputForm from 'components/InputForm';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FaEdit, FaEye, FaMailBulk } from 'react-icons/fa';
import { IoRefresh } from 'react-icons/io5';
import {
  cancelRecurrencePayment,
  createNewRecurrencePaymentRequest,
  doneRecurrencePayment,
  findClientPayments,
  newChargeForGeneratedRecurrence,
  sendMailRecurrencePaymentRequest,
  updateRecurrencePaymentDueDate,
} from 'services/api.service';
import { GeneratesRecurrence } from 'services/types/RecurrencyPayments.entity';
import { maskCurrency } from 'utils/number';

export type CreateRecurrencyForm = {
  amount: string;
  description: string;
  initDate: string;
  period: SelectOptionType;
  endDate: string;
};

type DueDateForm = {
  dueDate: string;
};

enum StatusDescription {
  PENDING = 'Pendente',
  CONFIRMED = 'Confirmado',
  CANCELED = 'Cancelado',
  INADIMPLANT = 'Inadimplente',
}

const SummaryCard: React.FC<{
  label: string;
  value: string | number;
  helper?: string;
}> = ({ label, value, helper }) => (
  <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4}>
    <Text fontSize="sm" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text fontSize="2xl" fontWeight="semibold" color="gray.800">
      {value}
    </Text>
    {helper ? (
      <Text fontSize="sm" color="gray.500" mt={1}>
        {helper}
      </Text>
    ) : null}
  </Box>
);

const EditDueDateButton: React.FC<{
  payment: GeneratesRecurrence;
  onSave: (recurrenceId: string, dueDate: string) => Promise<void>;
}> = ({ payment, onSave }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { control, handleSubmit, reset } = useForm<DueDateForm>();

  const openModal = () => {
    reset({
      dueDate: payment.charge?.expireAt ? DateTime.fromISO(payment.charge.expireAt).toFormat('yyyy-MM-dd') : '',
    });
    onOpen();
  };

  if (!payment.charge || payment.status !== 'PENDING') {
    return null;
  }

  return (
    <>
      <Tooltip label="Editar vencimento" aria-label="Editar vencimento">
        <IconButton
          aria-label="Editar vencimento"
          icon={<FaEdit />}
          size="sm"
          colorScheme="brand"
          variant="outline"
          onClick={openModal}
        />
      </Tooltip>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar vencimento</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <InputForm
              control={control}
              name="dueDate"
              label="Nova data de vencimento"
              type="date"
              rules={{
                required: 'Campo obrigatório',
              }}
            />
          </ModalBody>
          <ModalFooter>
            <HStack spacing={4}>
              <Button colorScheme="red" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="green"
                onClick={handleSubmit(async (data) => {
                  await onSave(payment.id, data.dueDate);
                  onClose();
                })}
              >
                Salvar
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const ShowPayments: React.FC<{
  payments?: GeneratesRecurrence[];
  recurrenceClientId: string;
  name: string;
  amount: string;
}> = ({ payments, recurrenceClientId, name, amount }) => {
  const toast = useToast();
  const { mutateAsync: sendEmail } = useMutation(sendMailRecurrencePaymentRequest);
  const { mutateAsync: done } = useMutation(doneRecurrencePayment);
  const { mutateAsync: cancel } = useMutation(cancelRecurrencePayment);
  const { mutateAsync: newCharge } = useMutation(newChargeForGeneratedRecurrence);
  const { mutateAsync: updateDueDate } = useMutation(updateRecurrencePaymentDueDate);

  const refresh = async () => {
    await queryClient.invalidateQueries([`findPaymentsRecurrency`, recurrenceClientId]);
  };

  const metrics = useMemo(() => {
    const items = payments ?? [];
    const pending = items.filter((item) => item.status === 'PENDING').length;
    const confirmed = items.filter((item) => item.status === 'CONFIRMED').length;
    const canceled = items.filter((item) => item.status === 'CANCELED').length;
    const totalAmount = items.reduce((acc, item) => acc + Number(item.charge?.amount ?? amount ?? 0), 0);

    return {
      total: items.length,
      pending,
      confirmed,
      canceled,
      totalAmount,
    };
  }, [amount, payments]);

  const handleSendEmail = async (recurrenceId: string) => {
    try {
      await sendEmail(recurrenceId);
      refresh();
      toast({
        title: 'E-mail enviado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: isAxiosError(error) ? error?.response?.data?.message : 'Erro ao enviar e-mail',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDone = async (recurrenceId: string) => {
    try {
      await done(recurrenceId);
      refresh();
      toast({
        title: 'Pagamento concluído com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: isAxiosError(error) ? error?.response?.data?.message : 'Erro ao concluir pagamento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCancel = async (recurrenceId: string) => {
    try {
      await cancel(recurrenceId);
      refresh();
      toast({
        title: 'Pagamento cancelado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: isAxiosError(error) ? error?.response?.data?.message : 'Erro ao cancelar pagamento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleNewCharge = async (recurrenceId: string) => {
    try {
      await newCharge(recurrenceId);
      refresh();
      toast({
        title: 'Cobrança gerada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: isAxiosError(error) ? error?.response?.data?.message : 'Erro ao gerar cobrança',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateDueDate = async (recurrenceId: string, dueDate: string) => {
    try {
      await updateDueDate({ id: recurrenceId, data: { dueDate } });
      refresh();
      toast({
        title: 'Vencimento atualizado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: isAxiosError(error) ? error?.response?.data?.message : 'Erro ao atualizar vencimento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (!payments?.length) {
    return (
      <Box border="1px dashed" borderColor="gray.300" borderRadius="xl" px={6} py={10} textAlign="center">
        <Text fontSize="lg" fontWeight="semibold" color="gray.700">
          Nenhuma cobrança gerada
        </Text>
        <Text color="gray.500" mt={2}>
          Quando houver lançamentos para este pagador, eles aparecerão aqui com status, vencimento e ações rápidas.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4} mb={6}>
        <SummaryCard label="Lançamentos" value={metrics.total} />
        <SummaryCard label="Pendentes" value={metrics.pending} helper={`${metrics.confirmed} confirmados`} />
        <SummaryCard label="Cancelados" value={metrics.canceled} />
        <SummaryCard label="Valor lançado" value={maskCurrency(metrics.totalAmount)} />
      </SimpleGrid>

      <TableContainer border="1px solid" borderColor="gray.200" borderRadius="xl">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Descrição</Th>
              <Th isNumeric>Valor</Th>
              <Th>Status</Th>
              <Th>Criação</Th>
              <Th>Vencimento</Th>
              <Th>Pagamento</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {payments.map((item) => (
              <Tr key={item.id}>
                <Td>{item.charge?.description ?? name ?? 'Pagamento manual'}</Td>
                <Td isNumeric>{maskCurrency(item.charge?.amount ?? amount)}</Td>
                <Td>
                  <Badge
                    colorScheme={item.status === 'CANCELED' ? 'red' : item.status === 'CONFIRMED' ? 'green' : 'orange'}
                  >
                    {StatusDescription[item.status]}
                  </Badge>
                </Td>
                <Td>{item.date ? DateTime.fromISO(item.date).toFormat('dd/MM/yyyy HH:mm') : '---'}</Td>
                <Td>{item.charge?.expireAt ? DateTime.fromISO(item.charge.expireAt).toFormat('dd/MM/yyyy HH:mm') : '---'}</Td>
                <Td>
                  {item.status === 'CONFIRMED'
                    ? DateTime.fromISO(item.charge?.payedAt ?? item.updatedAt).toFormat('dd/MM/yyyy HH:mm')
                    : '---'}
                </Td>
                <Td>
                  <Wrap spacing={2}>
                    <WrapItem>
                      <Tooltip label="Disparar e-mail de cobrança" aria-label="Disparar e-mail de cobrança">
                        <IconButton
                          isDisabled={item.status !== 'PENDING'}
                          aria-label="Disparar e-mail de cobrança"
                          icon={<FaMailBulk />}
                          size="sm"
                          colorScheme="brand"
                          onClick={() => {
                            handleSendEmail(item.id);
                          }}
                        />
                      </Tooltip>
                    </WrapItem>
                    <WrapItem>
                      <Tooltip label="Atualizar cobrança" aria-label="Atualizar cobrança">
                        <IconButton
                          aria-label="Atualizar cobrança"
                          icon={<IoRefresh />}
                          isDisabled={item.status !== 'PENDING'}
                          size="sm"
                          colorScheme="brand"
                          onClick={() => {
                            handleNewCharge(item.id);
                          }}
                        />
                      </Tooltip>
                    </WrapItem>
                    <WrapItem>
                      <EditDueDateButton payment={item} onSave={handleUpdateDueDate} />
                    </WrapItem>
                    <WrapItem>
                      <Tooltip label="Concluir cobrança" aria-label="Concluir cobrança">
                        <IconButton
                          aria-label="Concluir cobrança"
                          icon={<CheckIcon />}
                          size="sm"
                          isDisabled={!['PENDING', 'INADIMPLANT'].includes(item.status)}
                          colorScheme="brand"
                          onClick={() => {
                            handleDone(item.id);
                          }}
                        />
                      </Tooltip>
                    </WrapItem>
                    <WrapItem>
                      <Tooltip label="Cancelar cobrança" aria-label="Cancelar cobrança">
                        <IconButton
                          aria-label="Cancelar cobrança"
                          icon={<CloseIcon />}
                          isDisabled={!['PENDING', 'INADIMPLANT'].includes(item.status)}
                          size="sm"
                          colorScheme="brand"
                          onClick={() => {
                            handleCancel(item.id);
                          }}
                        />
                      </Tooltip>
                    </WrapItem>
                  </Wrap>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const ShowPaymentsRecurrence: React.FC<{
  clientRecurrencyId: string;
  oncloseMainModal: () => void;
  recurrencyName: string;
  amount: string;
}> = ({ clientRecurrencyId, oncloseMainModal, recurrencyName, amount }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { data, isLoading } = useQuery([`findPaymentsRecurrency`, clientRecurrencyId], () => findClientPayments(clientRecurrencyId), {
    enabled: isOpen,
  });
  const { mutateAsync: newRecurrencePayment, isLoading: isLoadingNewRecurrence } = useMutation(
    createNewRecurrencePaymentRequest,
  );

  const refreshData = async () => {
    await queryClient.invalidateQueries(['findPaymentsRecurrency', clientRecurrencyId]);
  };

  const handleCreateNewRecurrencePayment = async (recurrenceId: string, withoutCharge = false) => {
    try {
      await newRecurrencePayment({ id: recurrenceId, withoutCharge });
      toast({
        title: 'Pagamento gerado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      refreshData();
    } catch (error) {
      toast({
        title: isAxiosError(error) ? error?.response?.data?.message : 'Erro ao gerar pagamento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Tooltip label="Consultar cobranças">
        <Button onClick={onOpen} size="sm" rounded="sm" variant="outline" colorScheme="brand" leftIcon={<FaEye />}>
          Cobranças
        </Button>
      </Tooltip>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={() => {
            onClose();
            oncloseMainModal();
          }}
          size="6xl"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Consultar pagamentos de {data?.client?.name}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text color="gray.500" mb={6}>
                Acompanhe o histórico de cobrança deste pagador e use as ações rápidas para reemitir, enviar e-mail ou ajustar o vencimento.
              </Text>
              <ShowPayments
                payments={data?.generatesRecurrences}
                recurrenceClientId={clientRecurrencyId}
                name={recurrencyName}
                amount={amount}
              />
            </ModalBody>
            <ModalFooter>
              <Wrap spacing={3} justify="flex-end">
                <WrapItem>
                  <Button
                    colorScheme="red"
                    isLoading={isLoading}
                    onClick={() => {
                      onClose();
                      oncloseMainModal();
                    }}
                  >
                    Fechar
                  </Button>
                </WrapItem>
                <WrapItem>
                  <Button
                    colorScheme="green"
                    isLoading={isLoading || isLoadingNewRecurrence}
                    onClick={() => {
                      handleCreateNewRecurrencePayment(clientRecurrencyId);
                    }}
                  >
                    Criar cobrança
                  </Button>
                </WrapItem>
                <WrapItem>
                  <Button
                    colorScheme="green"
                    variant="outline"
                    isLoading={isLoading || isLoadingNewRecurrence}
                    onClick={() => {
                      handleCreateNewRecurrencePayment(clientRecurrencyId, true);
                    }}
                  >
                    Lançar pagamento
                  </Button>
                </WrapItem>
              </Wrap>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ShowPaymentsRecurrence;
